import { MaterialIcons } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, View, Text, FlatList, TextInput } from "react-native";

import i18n from "@/locales/i18n";
import { fiatTypeAccounts } from "@/utils/accounts/Utils";
import {
  checkIcon,
  checkAsset,
  checkAssetBlockchain,
} from "@/utils/transactions/ExchangeCurrencies";

interface InputCryptoProps {
  data?: any;
  valueConverted: number;
  currencyToConvert: string;
  setCurrencyToConvert: (value: string) => void;
  setAccountIdCrypto: (value: string) => void;
  setAmount?: (value: number) => void;
  createExchange?: (value: number) => void;
  availableBalance?: number;
  precision: number;
  blocked?: boolean;
  isRateFetching?: boolean;
}

export default function InputCrypto(props: InputCryptoProps) {
  const [amount, setAmount] = useState("");
  const [separator, setSeparator] = useState(",");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isCurrenciesListVisible, setIsCurrenciesListVisible] = useState(false);
  const [isMaxHovered, setIsMaxHovered] = useState(false);
  const language = i18n.language;

  const { t } = useTranslation();

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        console.log("entrou aqui! 3");
        setAmount("");
      };
    }, [])
  );

  useEffect(() => {
    if (!props.blocked) {
      console.log("entrou aqui! 1");
      setAmount(props.valueConverted.toString());
    }
  }, [props.blocked, props.valueConverted]);

  useEffect(() => {
    if (language && language === "en") {
      setSeparator(".");
    }
  }, [language]);

  function parseMoney(value: string): number {
    value = value.trim();
    value = value.replace(/,/g, ".");
    const parsed = parseFloat(value);

    if (isNaN(parsed)) {
      return;
    }

    return parseFloat(parsed.toFixed(props.precision));
  }

  const handleTextChange = (text) => {
    let filteredText = text.replace(/[^0-9.,]/g, "");

    if (separator === ",") {
      filteredText = filteredText.replace(/\./g, ",");
    } else {
      filteredText = filteredText.replace(/,/g, ".");
    }

    setAmount(filteredText);
    props.setAmount(parseMoney(filteredText));
  };

  return (
    <View className="z-50 items-center">
      <TextInput
        className={`border border-solid border-midgray rounded-xl w-80 h-14 text-black1 mt-2 mb-5 ${
          isCurrenciesListVisible
            ? "rounded-b-none border-b-0 rounded-tr-xl rounded-tl-xl"
            : "rounded-full"
        } ${isInputFocused ? "px-4 py-6" : "pl-4 py-6 pr-36"} ${
          amount.length > 10 ? "text-base" : "text-2xl"
        }`}
        value={amount}
        keyboardType="decimal-pad"
        onChangeText={handleTextChange}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
        numberOfLines={1}
        editable={props.blocked && !props.isRateFetching}
      />
      {isCurrenciesListVisible && (
        <FlatList
          data={props.data.filter(
            (item) =>
              !fiatTypeAccounts.includes(item.assetTypeId) &&
              item.status === "OPEN"
          )}
          className={`z-50 absolute top-14 w-80 max-h-32 bg-white1 border border-midgray rounded-bl-xl ${
            props.data.filter(
              (item) => !fiatTypeAccounts.includes(item.assetTypeId)
            ).length <= 3
              ? "rounded-br-xl"
              : ""
          }`}
          renderItem={({ item }) => (
            <Pressable
              className="bg-white1 hover:bg-gray flex-row p-1 items-center justify-center border-t border-b border-gray"
              onPress={() => {
                props.setAccountIdCrypto(item.id);
                props.setCurrencyToConvert(item.assetTypeId);
                setIsCurrenciesListVisible(false);
                setAmount("");
              }}
            >
              {checkIcon(item.assetTypeId)}
              <Text className="mx-1 text-base">
                {`${checkAsset(item.assetTypeId)}/${checkAssetBlockchain(
                  item.assetTypeId
                )}`}
              </Text>
            </Pressable>
          )}
        />
      )}
      {!isInputFocused && (
        <View className="absolute right-3 top-6 items-center flex-row">
          {props.blocked && (
            <Pressable
              className={`border rounded-full px-1 mr-2 ${
                isMaxHovered ? "border-purple1" : "border-midgray"
              }`}
              onHoverIn={() => setIsMaxHovered(true)}
              onHoverOut={() => setIsMaxHovered(false)}
              onPress={() => {
                setAmount(props.availableBalance.toString());
                props.setAmount(props.availableBalance);
              }}
            >
              <Text
                className={`text-xs ${
                  isMaxHovered ? "text-purple1" : "text-midgray"
                }`}
              >
                {t("common:terms.setmax").toUpperCase()}
              </Text>
            </Pressable>
          )}
          <Pressable
            className="flex-row items-center"
            onPress={() => setIsCurrenciesListVisible(!isCurrenciesListVisible)}
            disabled={props.isRateFetching}
          >
            <Text className="text-black text-base">
              {checkAsset(props.currencyToConvert)}
            </Text>
            <View className="ml-1.5 mr-0.5">
              {checkIcon(props.currencyToConvert)}
            </View>
            <MaterialIcons
              name="keyboard-arrow-down"
              size={16}
              color="#919191"
            />
          </Pressable>
        </View>
      )}
    </View>
  );
}
